import React, { useEffect, useRef, useState } from 'react';

type Props = {
  children: React.ReactNode;
  onRefresh: () => Promise<void> | void;
  isRefreshing: boolean;
  scrollElement: HTMLElement | null;
  className?: string;
};

const MAX_PULL = 110;
const THRESHOLD = 70;

const PullToRefresh = ({
  children,
  onRefresh,
  isRefreshing,
  scrollElement,
  className = '',
}: Props) => {
  const [currentY, setCurrentY] = useState(0);
  const [pulling, setPulling] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const pullingRef = useRef(false);
  const startYRef = useRef(0);
  const currentYRef = useRef(0);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    if (!scrollElement) return;

    const handleTouchStart = (e: TouchEvent) => {
      if (scrollElement.scrollTop <= 1) {
        startYRef.current = e.touches[0].clientY;
        pullingRef.current = true;
        setPulling(true);
      } else {
        pullingRef.current = false;
        setPulling(false);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!pullingRef.current) return;
      if (scrollElement.scrollTop > 1) {
        pullingRef.current = false;
        setPulling(false);
        currentYRef.current = 0;
        setCurrentY(0);
        return;
      }

      const y = e.touches[0].clientY;
      const diff = y - startYRef.current;

      if (diff > 0) {
        // Resistance effect: reduced to 1:1 for "no parallax" feel until max
        const move = Math.min(diff, MAX_PULL);
        currentYRef.current = move;
        setCurrentY(move);

        // Prevent default only if we are effectively pulling to refresh to avoid browser native refresh
        // Note: preventDefault() in passive listener is dependent on browser support/setup
        if (e.cancelable && diff > 5) {
          e.preventDefault();
        }
      }
    };

    const handleTouchEnd = () => {
      if (!pullingRef.current) return;
      pullingRef.current = false;
      setPulling(false);

      if (currentYRef.current > THRESHOLD) {
        onRefreshRef.current();
      } else {
        currentYRef.current = 0;
        setCurrentY(0);
      }
    };

    scrollElement.addEventListener('touchstart', handleTouchStart, { passive: true });
    scrollElement.addEventListener('touchmove', handleTouchMove, { passive: false });
    scrollElement.addEventListener('touchend', handleTouchEnd);

    return () => {
      scrollElement.removeEventListener('touchstart', handleTouchStart);
      scrollElement.removeEventListener('touchmove', handleTouchMove);
      scrollElement.removeEventListener('touchend', handleTouchEnd);
    };
  }, [scrollElement]);

  useEffect(() => {
    if (!isRefreshing) {
      currentYRef.current = 0;
      setCurrentY(0);
    } else {
      currentYRef.current = THRESHOLD;
      setCurrentY(THRESHOLD);
    }
  }, [isRefreshing]);

  const rotation = Math.min(currentY * 4, 360); // Rotate based on pull distance (90px * 4 = 360deg)

  return (
    <div className={`relative ${className}`}>
      {/* Loading Spinner / Indicator */}
      <div
        className="absolute left-0 w-full flex justify-center pointer-events-none z-50"
        style={{
          top: 0,
          transform: `translateY(${currentY}px)`,
          transition: pulling ? 'none' : 'transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          opacity: Math.min(currentY / (THRESHOLD / 2), 1)
        }}
      >
        <div className="bg-white rounded-full p-2 flex items-center justify-center w-10 h-10">
          {isRefreshing ? (
            <svg className="animate-spin h-7 w-7 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5"></circle>
              <path className="opacity-75" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" d="M12 2A10 10 0 1 1 2 12"></path>
            </svg>
          ) : (
            <svg
              className="h-7 w-7 text-primary transition-transform duration-0 ease-linear"
              style={{ transform: `rotate(${rotation}deg)` }}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          )}
        </div>
      </div>

      <div ref={contentRef}>
        {children}
      </div>
    </div>
  );
};

export default PullToRefresh;
